import React from 'react';
import { useCompanyName } from './useCompanyName';
import { formatInTenant, formatDateWords } from '../shared/tenantClock';

// System-wide report style tokens — every report's table header rule (and
// any other shared report styling added later) reads from here rather than
// hardcoding its own color, so a tweak like "make it dark grey, not near-
// black" only ever needs to happen in one place.
//
// Two table font sizes, not one — reports split into two purposes that want
// opposite things from type size (see
// docs/product/platform/architecture/pat-architecture-reports-ui-standard.md):
// - `tableFontSize` (11.5px) — admin/data tables: dense, many-column, scanned
//   for numbers, meant to fit a lot on the page width (Integrity Reports,
//   Cost Price History, Margin/Margin Matrix/Price Break, Price Lookup, and
//   a Quote's and Sales Order's own line-items tables — the two reports
//   actually printed to paper, where fitting more per page matters most).
// - `catalogFontSize` (14px) — customer-facing catalog documents, browsed
//   more like a printed catalog than mined for a number (Pricelist,
//   Product Detail/Summary).
export const reportStyle = {
  tableHeaderBorder: '1px solid #999',
  tableFontSize: 11.5,
  catalogFontSize: 14,
};

// Outlined-pill treatment shared by every report's toolbar button (Print,
// Download CSV, Close) — same shape/palette as ReportLauncher's "Print ▾"
// trigger, so a report's own controls read as one coherent toolbar rather
// than one styled button next to default browser buttons. Deliberately
// outlined, not filled, to keep reports visually distinct from forms (Save/
// Cancel are solid-filled) — a report produces a document, it doesn't commit
// data.
export function reportButtonStyle(disabled) {
  return {
    padding: '8px 14px',
    background: '#fff',
    color: '#1976d2',
    border: '1px solid #1976d2',
    borderRadius: 6,
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

// Standard "Generated <date>, <time>" stamp used by every report's
// subtitle — one wording and one precision (date + time), so reports don't
// drift into "As of <date only>" on one and "Generated <date+time>" on
// another.
export function formatReportTimestamp(date = new Date()) {
  const dateStr = formatDateWords(date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = formatInTenant(date, { hour: 'numeric', minute: '2-digit', hour12: true }, undefined);
  return `Generated ${dateStr}, ${timeStr}`;
}

// Canonical wrapper for every print/PDF report in this app (Product,
// Pricelist, and future Accounting/Sales/Inventory reports) — the
// print-area/no-print pattern, company name header, and title/subtitle
// hierarchy live here ONCE. New reports should render through this
// component instead of hand-rolling the pattern; that's what let the
// Product Detail/Summary report title size drift out of sync with
// Pricelist's in the first place.
//
// `title`/`subtitle` are optional — a single-record report (e.g. one
// Product) can omit them and let its own content supply the heading.
// `actions` is extra no-print buttons (e.g. "Download CSV") rendered
// between the built-in Print and Close buttons. `footerNote` is an optional
// extra print-only line (e.g. a "Generated ..." stamp a report wants in the
// footer instead of its subtitle) rendered inside the SAME running-footer
// box, above "Commercial in Confidence" — one position:fixed element, one
// border-top over both lines, rather than a second independently-fixed
// element a report would otherwise have to position itself (fragile; see
// App.css's note on why offsetting a repeating position:fixed element is
// unsafe in Chromium's print engine).
// `hideRunningHeader`: skips the repeating "Company - Title" strip a report
// otherwise gets on every printed page. Opt-in-to-hide, not the default —
// most reports have no letterhead of their own, so page 2+ would otherwise
// carry no identification at all. A report that already renders its own
// full company/title letterhead in its content (e.g. QuoteReportView.js's
// Supplied By block) would just be repeating that on page 1.
// `hideConfidentialityNote`: drops "Commercial in Confidence - Do Not Share"
// from the running footer. Opt-in-to-hide, not the default — most reports
// here are internal-only pulls (Pricelist, Product Detail) where that
// warning is the point; a Quote is a document meant to leave the building,
// so the warning doesn't apply and footerNote takes the full footer line
// instead of sharing it.
// `companyInfo` — see useCompanyName.js's own header comment; passed
// straight through so a server-rendered PDF (issue #238's Compose Email
// Pricelist attachment) can feed this shell pre-resolved data instead of
// letting it fetch its own.
function ReportShell({ title, subtitle, actions, onClose, printDisabled, footerNote, hideRunningHeader, hideConfidentialityNote, hideLogo, hideCompanyNameHeader, beforeTitle, sidePadding = 24, companyInfo, children }) {
  const { companyName, logoUrl } = useCompanyName(companyInfo);

  return (
    // --report-side-padding lets App.css's fixed running header/footer line up
    // with this left/right padding instead of a hard-coded 24px.
    <div className="print-area" style={{ background: '#fff', padding: 24, paddingLeft: sidePadding, paddingRight: sidePadding, borderRadius: 8, '--report-side-padding': `${sidePadding}px` }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => window.print()} disabled={printDisabled} style={reportButtonStyle(printDisabled)}>Print</button>
          {actions}
        </div>
        <div style={{ width: 1, alignSelf: 'stretch', background: '#ddd' }} />
        <button type="button" onClick={onClose} style={reportButtonStyle(false)}>Close</button>
      </div>

      {!hideRunningHeader && (
        <div className="print-only print-running-header">{companyName}{companyName && title ? ' - ' : ''}{title}</div>
      )}

      {beforeTitle}

      {/* Logo sits beside the heading block, at the right edge, instead of
          stacked above it - sharing the row means it only adds height if
          it's taller than the text block, rather than always pushing
          everything below it down. Also deliberately NOT inside
          .print-running-header above: that element is position:fixed and
          repeats on every continuation page - App.css documents Chromium's
          print pipeline double-painting it on multi-page reports when its
          content changes, and an image is a far more visible way for that
          to go wrong than the text ever was. Rendered here it appears once,
          at the top of page 1 only, like a letterhead. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Always rendered (even before useCompanyName's fetch resolves) so
              this line's height is reserved from the first paint — otherwise it
              pops in and bumps the title/subtitle below it down once loaded. */}
          {!hideCompanyNameHeader && (
            <div className="no-print" style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 4 }}>{companyName || ' '}</div>
          )}

          {title && <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>{title}</h2>}
          {subtitle && <div style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>{subtitle}</div>}
        </div>

        {logoUrl && !hideLogo && (
          <img src={logoUrl} alt={companyName || 'Company logo'} style={{ maxHeight: 48, maxWidth: 220, objectFit: 'contain', flexShrink: 0 }} />
        )}
      </div>

      {children}

      <div className="print-only print-running-footer">
        {footerNote && <div style={{ marginBottom: hideConfidentialityNote ? 0 : 2 }}>{footerNote}</div>}
        {!hideConfidentialityNote && <div>Commercial in Confidence - Do Not Share</div>}
      </div>
    </div>
  );
}

export default ReportShell;
